import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "../../../components/layout/DashboardLayout";
import AceAvatar from "../../../components/shared/AceAvatar";
import UsageLineChart from "../../../components/charts/UsageLineChart";
import { getStatsData } from "../../../services/statsService";

const RANGE_OPTIONS = ["Heute", "Wöchentlich", "Monatlich", "Jährlich"];

const EMPTY_STATS = {
  classes: [],
  total: 0,
  avgStress: 0,
  elevatedCount: 0,
  elevatedPct: 0,
  activeCount: 0,
  completionRate: 0,
  stressTrend: [],
  activeTrend: [],
  pieSlices: [],
  classRows: [],
  userRows: [],
  bars: [],
};

function getRangeLabel(range) {
  const now = new Date();

  if (range === "Heute") {
    return now.toLocaleDateString("de-AT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  if (range === "Wöchentlich") {
    const firstJan = new Date(now.getFullYear(), 0, 1);
    const days = Math.floor((now - firstJan) / (24 * 60 * 60 * 1000));
    const week = Math.ceil((days + firstJan.getDay() + 1) / 7);
    return `KW ${week} / ${now.getFullYear()}`;
  }

  if (range === "Monatlich") {
    const label = now.toLocaleDateString("de-AT", {
      month: "long",
      year: "numeric",
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  if (range === "Jährlich") {
    return String(now.getFullYear());
  }

  return range;
}

function getPreviousRangeLabel(range) {
  const now = new Date();

  if (range === "Heute") {
    const previous = new Date(now);
    previous.setDate(previous.getDate() - 1);

    return previous.toLocaleDateString("de-AT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  if (range === "Wöchentlich") {
    const previous = new Date(now);
    previous.setDate(previous.getDate() - 7);

    const firstJan = new Date(previous.getFullYear(), 0, 1);
    const days = Math.floor((previous - firstJan) / (24 * 60 * 60 * 1000));
    const week = Math.ceil((days + firstJan.getDay() + 1) / 7);

    return `KW ${week} / ${previous.getFullYear()}`;
  }

  if (range === "Monatlich") {
    const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const label = previous.toLocaleDateString("de-AT", {
      month: "long",
      year: "numeric",
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  if (range === "Jährlich") {
    return String(now.getFullYear() - 1);
  }

  return "Vergleichszeitraum";
}

function StatsGlyph({ type }) {
  if (type === "stress") {
    return (
      <svg viewBox="0 0 24 24" className="ac-stats-glyph" aria-hidden="true">
        <path
          d="M12 4.5a4 4 0 0 1 4 4v2.2c0 1 .3 2 .9 2.8l1 1.4c.5.8 0 1.9-1 1.9H7.1c-1 0-1.5-1.1-1-1.9l1-1.4c.6-.8.9-1.8.9-2.8V8.5a4 4 0 0 1 4-4Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M10 18.5a2.2 2.2 0 0 0 4 0"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (type === "users") {
    return (
      <svg viewBox="0 0 24 24" className="ac-stats-glyph" aria-hidden="true">
        <circle cx="9" cy="9" r="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="16.5" cy="10" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="M4.5 18c.9-2.7 3-4 5.5-4s4.6 1.3 5.5 4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M14.5 17.5c.6-1.8 2-2.8 4-2.8 1 0 1.8.2 2.5.7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (type === "challenge") {
    return (
      <svg viewBox="0 0 24 24" className="ac-stats-glyph" aria-hidden="true">
        <path
          d="M7 6.5h10v2.2c0 2.4-1.2 4.6-3.2 6l-1.8 1.3-1.8-1.3A7.3 7.3 0 0 1 7 8.7V6.5Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M9.5 4.5h5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M10 10.8l1.3 1.3 2.8-2.8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className="ac-stats-glyph" aria-hidden="true">
      <path d="M5 18.5h14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M7.5 16V12.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M12 16V9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M16.5 16V6.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function LivePill({ tone = "green" }) {
  return <div className={`ac-stats-livepill ${tone}`}>Live</div>;
}

function KpiCard({
  title,
  value,
  sub,
  iconType,
  tone = "green",
  pulse = false,
  compact = false,
}) {
  return (
    <div
      className={[
        "ac-soft-panel ac-stats-kpi",
        pulse ? "is-pulse" : "",
        compact ? "is-compact" : "",
      ].join(" ").trim()}
    >
      <div className="ac-stats-kpi-top">
        <div className="ac-stats-kpi-left">
          <div className="ac-stats-kpi-icon">
            <StatsGlyph type={iconType} />
          </div>

          <div>
            <div className="ac-stats-kpi-title">{title}</div>
            <div className="ac-stats-kpi-sub">{sub}</div>
          </div>
        </div>

        <LivePill tone={tone} />
      </div>

      <div className="ac-stats-kpi-value">{value}</div>
    </div>
  );
}

function StatsBlock({ title, subtitle, children, right = null, pulse = false }) {
  return (
    <div className={["ac-soft-panel ac-stats-block", pulse ? "ac-page-enter" : ""].join(" ").trim()}>
      <div className="ac-stats-block-head">
        <div>
          <div className="ac-stats-block-title">{title}</div>
          {subtitle ? <div className="ac-stats-block-subtitle">{subtitle}</div> : null}
        </div>
        {right}
      </div>

      <div className="mt-4">{children}</div>
    </div>
  );
}

function StatusPill({ label, tone }) {
  const toneClassMap = {
    ok: "is-ok",
    watch: "is-watch",
    info: "is-info",
  };

  return <span className={`ac-stats-statuspill ${toneClassMap[tone] || ""}`}>{label}</span>;
}

function AnimatedDonutChart({ slices, animateKey }) {
  const visibleSlices = slices.filter((slice) => slice.value > 0);
  const total = visibleSlices.reduce((sum, slice) => sum + slice.value, 0);

  const cx = 90;
  const cy = 90;
  const r = 58;
  const strokeWidth = 22;
  const circumference = 2 * Math.PI * r;

  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const duration = 1400;

    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setProgress(eased);

      if (p < 1) {
        raf = requestAnimationFrame(tick);
      }
    };

    setProgress(0);
    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, [animateKey]);

  if (total <= 0) {
    return (
      <div className="ac-stats-donut-wrap">
        <div className="relative">
          <svg width="180" height="180" viewBox="0 0 180 180">
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke="#EEF2F7"
              strokeWidth={strokeWidth}
            />
          </svg>

          <div className="ac-stats-donut-center">
            <div className="ac-stats-donut-total">0</div>
            <div className="ac-stats-donut-total-sub">gesamt</div>
          </div>
        </div>
      </div>
    );
  }

  let cumulative = 0;

  const circles = visibleSlices.map((slice, index) => {
    const fraction = slice.value / total;
    const dash = fraction * circumference * progress;
    const gap = circumference - dash;
    const offset = circumference - cumulative;

    cumulative += fraction * circumference * progress;

    return (
      <circle
        key={index}
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={slice.color}
        strokeWidth={strokeWidth}
        strokeDasharray={`${dash} ${gap}`}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${cx} ${cy})`}
      />
    );
  });

  return (
    <div className="ac-stats-donut-wrap">
      <div className="relative">
        <svg width="180" height="180" viewBox="0 0 180 180">
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="#EEF2F7"
            strokeWidth={strokeWidth}
          />
          {circles}
        </svg>

        <div className="ac-stats-donut-center">
          <div className="ac-stats-donut-total">{Math.round(total)}</div>
          <div className="ac-stats-donut-total-sub">gesamt</div>
        </div>
      </div>

      <div className="ac-stats-donut-legend">
        {visibleSlices.map((slice, index) => {
          const pct = Math.round((slice.value / total) * 100);

          return (
            <div key={index} className="ac-stats-donut-legend-item">
              <span
                className="ac-stats-donut-legend-dot"
                style={{ background: slice.color }}
              />
              <div className="ac-stats-donut-legend-label">{slice.label}</div>
              <div className="ac-stats-donut-legend-meta">
                {slice.value} · {pct}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RoundedBarChart({ bars, titleType = "class" }) {
  const max = Math.max(...bars.map((bar) => bar.value), 1);

  return (
    <div className="ac-stats-barcard">
      <div className="ac-stats-barstage">
        {bars.map((bar, index) => {
          const height = Math.max((bar.value / max) * 220, 18);

          return (
            <div key={index} className="ac-stats-baritem">
              <div className="ac-stats-barvalue">
                {String(bar.value).replace(".", ",")}
              </div>

              <div
                className="ac-stats-barfill"
                style={{ height: `${height}px` }}
                title={`${bar.label}: ${bar.value}`}
              />

              <div className="ac-stats-barlabel">{bar.label}</div>
              <div className="ac-stats-barsub">
                {titleType === "class" ? "Klasse" : "User"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function StatsPage() {
  const navigate = useNavigate();

  const [range, setRange] = useState("Wöchentlich");
  const [selectedClass, setSelectedClass] = useState("Alle");
  const [pulse, setPulse] = useState(false);
  const [statsData, setStatsData] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);

  const statsNavState = useMemo(
    () => ({
      fromStats: true,
      backTo: "/stats",
      backLabel: "← Zurück zu Statistiken",
    }),
    []
  );

  useEffect(() => {
    setPulse(true);
    const timeoutId = setTimeout(() => setPulse(false), 320);
    return () => clearTimeout(timeoutId);
  }, [range, selectedClass]);

  useEffect(() => {
    let mounted = true;

    async function loadStats() {
      try {
        setLoading(true);
        const data = await getStatsData(range, selectedClass);

        if (!mounted) return;
        setStatsData(data);
      } catch (error) {
        console.error("Stats konnten nicht geladen werden:", error);

        if (!mounted) return;
        setStatsData(EMPTY_STATS);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadStats();

    return () => {
      mounted = false;
    };
  }, [range, selectedClass]);

  const periodLabel = useMemo(() => getRangeLabel(range), [range]);
  const previousPeriodLabel = useMemo(() => getPreviousRangeLabel(range), [range]);

  const { barTitle, barSubtitle } = useMemo(() => {
    if (selectedClass === "Alle") {
      return {
        barTitle: "Durchschnittlicher Stresswert pro Klasse",
        barSubtitle: "Vergleich der durchschnittlichen Belastung je Klasse",
      };
    }

    return {
      barTitle: `Stresswert pro Schüler:innen · ${selectedClass}`,
      barSubtitle: "Einzelansicht der ausgewählten Klasse",
    };
  }, [selectedClass]);

  const { overviewTitle, overviewSubtitle } = useMemo(() => {
    if (selectedClass === "Alle") {
      return {
        overviewTitle: "Klassenübersicht – Auffälligkeiten",
        overviewSubtitle: "Schneller Überblick über Belastung und Aktivität je Klasse",
      };
    }

    return {
      overviewTitle: `Userübersicht – ${selectedClass}`,
      overviewSubtitle:
        "Schneller Überblick über Belastung und Aktivität innerhalb der ausgewählten Klasse",
    };
  }, [selectedClass]);

  const quickCards = useMemo(
    () => [
      {
        title: "Auswahl",
        value: selectedClass === "Alle" ? "Alle Klassen" : selectedClass,
        sub: "Aktueller Filter",
        iconType: "chart",
        tone: "blue",
      },
      {
        title: "Zeitraum",
        value: periodLabel,
        sub: range,
        iconType: "chart",
        tone: "green",
      },
      {
        title: "Tendenz",
        value:
          statsData.elevatedPct >= 40
            ? "Auffällig"
            : statsData.elevatedPct >= 20
            ? "Beobachten"
            : "Stabil",
        sub: "Kurzbewertung",
        iconType: "stress",
        tone:
          statsData.elevatedPct >= 40
            ? "orange"
            : statsData.elevatedPct >= 20
            ? "blue"
            : "green",
      },
    ],
    [selectedClass, periodLabel, range, statsData.elevatedPct]
  );

  return (
    <DashboardLayout title="Statistiken">
      <div className="space-y-5">
        <section className="ac-soft-panel ac-stats-hero">
          <div className="ac-stats-hero-grid">
            <div>
              <h2 className="ac-stats-hero-title">
                {loading ? "Statistiken werden geladen..." : "Statistiken"}
              </h2>
              <p className="ac-stats-hero-text">
                Hier siehst du Belastung, Nutzung und Aktivität im Überblick.
              </p>
              <p className="ac-stats-hero-text" style={{ marginTop: 4 }}>
                Aktueller Zeitraum: {periodLabel}
              </p>
            </div>

            <div className="ac-stats-hero-side">
              <div className="ac-stats-acebox">
                <AceAvatar
                  variant={statsData.elevatedCount >= 2 ? "stress1" : "ruhig2"}
                  size={56}
                />
                <div className="ac-stats-acebubble">
                  Ace sagt: Gute Statistiken zeigen nicht nur Zahlen, sondern auch,
                  wo du genauer hinschauen solltest.
                </div>
              </div>
            </div>
          </div>

          <div className="ac-stats-filterbar">
            <div className="ac-stats-filterlabel">Filter</div>

            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="ac-select lg:w-[220px]"
            >
              {RANGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="ac-select lg:w-[220px]"
            >
              <option value="Alle">Alle Klassen</option>
              {statsData.classes.map((className) => (
                <option key={className} value={className}>
                  {className}
                </option>
              ))}
            </select>
          </div>

          <div
            key={`${range}-${selectedClass}-kpis`}
            className="ac-stats-kpi-grid ac-page-enter"
          >
            <KpiCard
              title="Durchschnittlicher Stresswert"
              value={String(statsData.avgStress).replace(".", ",")}
              sub={`Zeitraum: ${periodLabel}`}
              iconType="stress"
              tone="blue"
              pulse={pulse}
            />
            <KpiCard
              title="Belastete Nutzer:innen"
              value={`${statsData.elevatedPct}%`}
              sub={`${statsData.elevatedCount} kritische Logs`}
              iconType="stress"
              tone="orange"
              pulse={pulse}
            />
            <KpiCard
              title="Aktive Nutzer:innen"
              value={statsData.activeCount}
              sub="Im Zeitraum aktiv"
              iconType="users"
              tone="green"
              pulse={pulse}
            />
            <KpiCard
              title="Erledigungsquote"
              value={`${statsData.completionRate}%`}
              sub="Tasks im Zeitraum"
              iconType="challenge"
              tone="green"
              pulse={pulse}
            />
          </div>
        </section>

        <section className="ac-stats-grid-2">
          <StatsBlock
            title="Stressverlauf"
            subtitle={`${periodLabel} vs. ${previousPeriodLabel}`}
            pulse={pulse}
            right={
              <div className="ac-legend">
                <div className="ac-legend-item">
                  <span className="ac-legend-line is-green" />
                  <span>{periodLabel}</span>
                </div>
                <div className="ac-legend-item">
                  <span className="ac-legend-line is-blue" />
                  <span>{previousPeriodLabel}</span>
                </div>
              </div>
            }
          >
            <div key={`${range}-${selectedClass}-stress`} className="ac-page-enter">
              <UsageLineChart
                data={statsData.stressTrend}
                unitLabel="Stresswert"
                currentLabel={periodLabel}
                previousLabel={previousPeriodLabel}
              />
            </div>
          </StatsBlock>

          <StatsBlock
            title="Stressverteilung"
            subtitle="Verhältnis der Belastungszustände in der aktuellen Auswahl"
            pulse={pulse}
          >
            <div key={`${range}-${selectedClass}-pie`} className="ac-page-enter">
              <AnimatedDonutChart
                slices={statsData.pieSlices}
                animateKey={`${range}-${selectedClass}`}
              />
            </div>
          </StatsBlock>
        </section>

        <StatsBlock title={barTitle} subtitle={barSubtitle} pulse={pulse}>
          <div key={`${range}-${selectedClass}-bars`} className="ac-page-enter">
            <RoundedBarChart
              bars={statsData.bars}
              titleType={selectedClass === "Alle" ? "class" : "user"}
            />
          </div>
        </StatsBlock>

        <StatsBlock
          title="Kurzbewertung"
          subtitle="Schneller Überblick über die aktuelle Auswahl"
          pulse={pulse}
        >
          <div
            key={`${range}-${selectedClass}-quick`}
            className="ac-stats-quickgrid ac-page-enter"
          >
            {quickCards.map((card) => (
              <KpiCard
                key={card.title}
                title={card.title}
                value={card.value}
                sub={card.sub}
                iconType={card.iconType}
                tone={card.tone}
                pulse={pulse}
                compact
              />
            ))}
          </div>
        </StatsBlock>

        <StatsBlock
          title="Aktive Nutzer über Zeit"
          subtitle={`${periodLabel} vs. ${previousPeriodLabel}`}
          pulse={pulse}
          right={
            <div className="ac-legend">
              <div className="ac-legend-item">
                <span className="ac-legend-line is-green" />
                <span>{periodLabel}</span>
              </div>
              <div className="ac-legend-item">
                <span className="ac-legend-line is-blue" />
                <span>{previousPeriodLabel}</span>
              </div>
            </div>
          }
        >
          <div key={`${range}-${selectedClass}-active`} className="ac-page-enter">
            <UsageLineChart
              data={statsData.activeTrend}
              unitLabel="Nutzer"
              currentLabel={periodLabel}
              previousLabel={previousPeriodLabel}
            />
          </div>
        </StatsBlock>

        <section className="ac-soft-panel ac-stats-block">
          <div className="ac-stats-block-head">
            <div>
              <div className="ac-stats-block-title">{overviewTitle}</div>
              <div className="ac-stats-block-subtitle">{overviewSubtitle}</div>
            </div>
          </div>

          <div className="ac-stats-tablewrap">
            {selectedClass === "Alle" ? (
              <table className="ac-stats-table">
                <thead>
                  <tr>
                    <th>Klasse</th>
                    <th>Ø Stress</th>
                    <th>% belastet</th>
                    <th>Aktive Nutzer</th>
                    <th>Erledigte Tasks</th>
                    <th>Status</th>
                    <th>Details</th>
                  </tr>
                </thead>

                <tbody>
                  {statsData.classRows.map((row) => (
                    <tr key={row.id}>
                      <td className="font-extrabold text-ac-text">{row.klasse}</td>
                      <td>{String(row.avg).replace(".", ",")}</td>
                      <td>{row.elevatedPct}%</td>
                      <td>{row.active}</td>
                      <td>{row.done}</td>
                      <td>
                        <StatusPill label={row.status} tone={row.tone} />
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/users-classes/classes/${row.id}`, {
                              state: statsNavState,
                            })
                          }
                          className="ac-ghostbtn ac-stats-tablebtn"
                        >
                          Klasse öffnen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="ac-stats-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Ø Stress</th>
                    <th>Status</th>
                    <th>Aktivität</th>
                    <th>Erledigte Tasks</th>
                    <th>Details</th>
                  </tr>
                </thead>

                <tbody>
                  {statsData.userRows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div className="ac-stats-personname">{row.name}</div>
                        <div className="ac-stats-personmeta">{row.email}</div>
                      </td>
                      <td>{String(row.avg).replace(".", ",")}</td>
                      <td>{row.status}</td>
                      <td>{row.active}</td>
                      <td>{row.done}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/users-classes/users/${row.id}`, {
                              state: statsNavState,
                            })
                          }
                          className="ac-ghostbtn ac-stats-tablebtn"
                        >
                          User öffnen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="ac-stats-actions-row">
            <button
              type="button"
              onClick={() => navigate("/users-classes")}
              className="ac-ghostbtn ac-stats-smallbtn"
            >
              Zu User & Klassen
            </button>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}